/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description Submit Button for formily
 */
// TODO
import { useEffect, useState, useMemo } from 'react';
import { Tooltip } from 'antd';
import _ from 'lodash';
import { ISubmitProps, Submit } from '@formily/antd-v5';
import { useFormEffects } from '@formily/react';
import { transliterate } from 'transliteration';
import { onFieldMount, onFieldValueChange, onFormInitialValuesChange, onFormMount, onFormValuesChange } from '@formily/core';
import { usePersistenceStoreActions } from 'store/persistence';
import { scrollToFormErrorField } from '@veritable/common/utils';

export const cleanFormData = (formData: AnyType): AnyType => {
	if (_.isArray(formData)) {
		return formData.map(cleanFormData);
	}
	if (_.isObject(formData) && formData !== null) {
		const newObj = _.omitBy(formData, (__, key) => key.startsWith('__DO_NOT_USE_THIS_PROPERTY_'));
		return _.mapValues(newObj, (value) => cleanFormData(value));
	}
	return formData;
};

interface IFormDetails {
	values: AnyRecord;
	error: boolean;
}

interface IProps extends React.PropsWithChildren<ISubmitProps> {
	alwaysAllowNavigation?: boolean;
	isEditForm?: boolean;
	initialModel: AnyRecord;
	fieldsToTranslate?: string[];
	tooltipText?: string;
	checkChangedField?: string;
	getDynamicTitle?: (formValues: AnyRecord, defaultTitle?: string) => { disableSubmit: boolean; title: string };
}

const SubmitField: React.FC<IProps> = ({
	title,
	isEditForm,
	alwaysAllowNavigation,
	initialModel,
	disabled,
	loading,
	fieldsToTranslate,
	tooltipText,
	onSubmit,
	checkChangedField,
	getDynamicTitle,
	...restProps
}) => {
	const { setAllowNavigation } = usePersistenceStoreActions();
	const [formDetails, setFormDetails] = useState<IFormDetails>({
		values: initialModel,
		error: false,
	});
	const [initialValues, setInitialValues] = useState<AnyRecord>(initialModel);
	useFormEffects(() => {
		onFormValuesChange((form) => {
			setTimeout(() => {
				setFormDetails({ values: form.values, error: !_.isEmpty(form.errors) });
			});
		});
		onFormInitialValuesChange((form) => {
			setTimeout(() => {
				setInitialValues(_.cloneDeep(form.initialValues));
			});
		});

		onFormMount((form) => {
			setTimeout(() => {
				setFormDetails({ values: form.values, error: !_.isEmpty(form.errors) });
			});
		});
		if (checkChangedField) {
			onFieldValueChange(checkChangedField, (__, form) => {
				setFormDetails({ ...formDetails, values: form.values, error: !_.isEmpty(form.errors) });
			});
		}
		if (fieldsToTranslate && !_.isEmpty(fieldsToTranslate)) {
			_.forEach(fieldsToTranslate, (fieldPath) => {
				onFieldMount(fieldPath, (field) => {
					const onInputActions = field?.componentProps?.onInput;
					field.componentProps.onInput = (e: React.ChangeEvent<HTMLInputElement>) => {
						if (e.target instanceof HTMLInputElement) {
							e.target.value = transliterate(e.target.value || '');
							if (_.isFunction(onInputActions)) {
								onInputActions(e);
							}
						}
					};
				});
			});
		}
	});

	const disableSubmit = useMemo(() => {
		const cleanedFormValues = cleanFormData(formDetails.values);
		const cleanedInitialModel = cleanFormData(initialValues);
		return !!(disabled || (!isEditForm && _.isEqual(cleanedFormValues, cleanedInitialModel)) || formDetails.error);
	}, [formDetails, disabled, initialValues]);
	useEffect(() => {
		const shouldAllow = alwaysAllowNavigation || (!formDetails.error && disableSubmit && !loading);
		// prevent tab close & reload
		globalThis.onbeforeunload = () => (shouldAllow ? null : true);
		setAllowNavigation(shouldAllow);
		return () => {
			globalThis.onbeforeunload = null;
			setAllowNavigation(true);
		};
	}, [formDetails.error, disableSubmit, loading, alwaysAllowNavigation]);

	const { disableSubmit: dynamicDisableSubmit, title: dynamicTitle } = (() => {
		if (_.isFunction(getDynamicTitle)) {
			return getDynamicTitle(formDetails.values, title);
		}
		return { disableSubmit: false, title };
	})();

	return (
		<Tooltip title={disableSubmit ? tooltipText : ''} placement='topRight'>
			<Submit
				loading={loading}
				disabled={dynamicDisableSubmit || disableSubmit}
				onSubmit={onSubmit}
				onSubmitFailed={() => setFormDetails({ ...formDetails, error: true })}
				onClickCapture={scrollToFormErrorField}
				{...restProps}
			>
				{dynamicTitle ?? 'Save'}
			</Submit>
		</Tooltip>
	);
};

export default SubmitField;
